<template>
    <div class="equipment-profile-page" :class="{ 'profile-rtl': isRTL }">
        <div v-if="!equipment" class="profile-empty">
            <p>{{ $t('message.noEquipmentSelected') }}</p>
            <b-button variant="primary" @click="$router.push('/equipments')">{{ $t('message.backToEquipments') }}</b-button>
        </div>
        <template v-else>
            <div class="slide">
                <div class="bg-img">
                    <div class="slide-content">
                        <div><h2 class="slide-header">{{ $t('message.equipmentProfile') }}</h2></div>
                        <div>
                            <h5 class="slide-h5">{{ $t('message.home') }}</h5>
                            <b-icon :icon="isRTL ? 'chevron-double-left' : 'chevron-double-right'" class="icon-slider"></b-icon>
                            <h5 class="slide-h5">{{ $t('message.equipments') }}</h5>
                            <b-icon :icon="isRTL ? 'chevron-double-left' : 'chevron-double-right'" class="icon-slider"></b-icon>
                            <h5 class="slide-h5">{{ $t('message.equipmentProfile') }}</h5>
                        </div>
                    </div>
                </div>
            </div>

            <div class="container custom-container-orders mt-4 mb-5">
                <div class="profile-back-row mb-4">
                    <b-button variant="outline-primary" class="back-btn" @click="$router.push('/equipments')">
                        <b-icon :icon="isRTL ? 'chevron-right' : 'chevron-left'"></b-icon>
                        {{ $t('message.backToEquipments') }}
                    </b-button>
                </div>

                <!-- صورة المعدة -->
                <div class="profile-image-section">
                    <div class="profile-image-wrapper">
                        <img v-if="equipmentImageUrl" :src="equipmentImageUrl" :alt="equipment.name" class="profile-image" />
                        <div v-else class="profile-image-placeholder">
                            <b-icon icon="image-alt" font-scale="4" class="placeholder-icon"></b-icon>
                            <span class="placeholder-text">{{ $t('message.equipmentImage') }}</span>
                        </div>
                    </div>
                </div>

                <!-- أقسام المعلومات -->
                <div class="profile-sections">
                    <!-- القسم الأساسي -->
                    <div class="profile-section-card">
                        <h3 class="section-title">
                            <b-icon icon="info-circle-fill" class="section-icon"></b-icon>
                            {{ $t('message.basicInformation') }}
                        </h3>
                        <div class="section-grid">
                            <div class="section-item">
                                <span class="section-label">{{ $t('message.equipmentNumber') }}</span>
                                <span class="section-value">#{{ equipment.equpmentId }}</span>
                            </div>
                            <div class="section-item">
                                <span class="section-label">{{ $t('message.equipmentName') }}</span>
                                <span class="section-value section-value-bold">{{ equipment.name }}</span>
                            </div>
                            <div class="section-item">
                                <span class="section-label">{{ $t('message.serialNumber') }}</span>
                                <span class="section-value section-value-mono">{{ equipment.serial_number }}</span>
                            </div>
                            <div class="section-item">
                                <span class="section-label">{{ $t('message.status') }}</span>
                                <span class="section-value">
                                    <span class="status-pill">{{ equipment.equipment_status }}</span>
                                </span>
                            </div>
                            <div class="section-item">
                                <span class="section-label">{{ $t('message.createdDate') }}</span>
                                <span class="section-value">{{ equipment.created_date || '—' }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- البيانات الفنية -->
                    <div class="profile-section-card">
                        <h3 class="section-title">
                            <b-icon icon="gear-fill" class="section-icon"></b-icon>
                            {{ $t('message.technicalData') }}
                        </h3>
                        <div class="section-grid">
                            <div class="section-item">
                                <span class="section-label">{{ $t('message.equipmentType') }}</span>
                                <span class="section-value">{{ equipment.equipment_type || '—' }}</span>
                            </div>
                            <div class="section-item">
                                <span class="section-label">{{ $t('message.equipmentModel') }}</span>
                                <span class="section-value">{{ equipment.equipment_model || '—' }}</span>
                            </div>
                            <div class="section-item">
                                <span class="section-label">{{ $t('message.equipmentManufactor') }}</span>
                                <span class="section-value">{{ equipment.equipment_manufactor || '—' }}</span>
                            </div>
                            <div class="section-item" v-if="equipment.equipment_family_id">
                                <span class="section-label">{{ $t('message.equipmentFamily') }}</span>
                                <span class="section-value">{{ equipment.equipment_family_id }}</span>
                            </div>
                            <div class="section-item" v-if="equipment.parentEupmentId && equipment.parentEupmentId !== '0'">
                                <span class="section-label">{{ $t('message.parentEquipment') }}</span>
                                <span class="section-value">{{ equipment.parentEupmentId }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- التواريخ -->
                    <div class="profile-section-card">
                        <h3 class="section-title">
                            <b-icon icon="calendar3" class="section-icon"></b-icon>
                            {{ $t('message.datesSection') }}
                        </h3>
                        <div class="section-grid">
                            <div class="section-item">
                                <span class="section-label">{{ $t('message.startDate') }}</span>
                                <span class="section-value">{{ equipment.start_date || '—' }}</span>
                            </div>
                            <div class="section-item">
                                <span class="section-label">{{ $t('message.lastPm') }}</span>
                                <span class="section-value">{{ equipment.last_pm || '—' }}</span>
                            </div>
                            <div class="section-item">
                                <span class="section-label">{{ $t('message.nextPm') }}</span>
                                <span class="section-value">{{ equipment.next_pm || '—' }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- الصيانة الدورية -->
                    <div class="profile-section-card">
                        <h3 class="section-title">
                            <b-icon icon="calendar-check" class="section-icon"></b-icon>
                            {{ $t('message.maintenanceSchedule') }}
                        </h3>
                        <div class="section-grid section-grid-inline">
                            <span v-if="equipment.daily === '1'" class="schedule-tag">{{ $t('message.daily') }}</span>
                            <span v-if="equipment.weekly === '1'" class="schedule-tag">{{ $t('message.weekly') }}</span>
                            <span v-if="equipment.monthly === '1'" class="schedule-tag">{{ $t('message.monthly') }}</span>
                            <span v-if="equipment.yearly === '1'" class="schedule-tag">{{ $t('message.yearly') }}</span>
                            <span v-if="!hasMaintenanceSchedule" class="section-value">—</span>
                        </div>
                    </div>

                    <!-- التكلفة والملاحظات -->
                    <div class="profile-section-card">
                        <h3 class="section-title">
                            <b-icon icon="journal-text" class="section-icon"></b-icon>
                            {{ $t('message.costAndNotes') }}
                        </h3>
                        <div class="section-grid">
                            <div class="section-item" v-if="equipment.cost != null">
                                <span class="section-label">{{ $t('message.equipmentCost') }}</span>
                                <span class="section-value">{{ equipment.cost }}</span>
                            </div>
                            <div class="section-item section-item-full" v-if="equipment.additional_note">
                                <span class="section-label">{{ $t('message.additionalNotes') }}</span>
                                <p class="section-value section-notes">{{ equipment.additional_note }}</p>
                            </div>
                            <div v-if="!equipment.cost && !equipment.additional_note" class="section-value">—</div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';

const API_BASE = 'https://roomaclass.com/maintenance_new/';

export default {
    name: 'EquipmentProfile',
    computed: {
        ...mapGetters(['getSelectedEquipment']),
        equipment() {
            return this.getSelectedEquipment;
        },
        isRTL() {
            return this.$i18n.locale === 'ar';
        },
        equipmentImageUrl() {
            const e = this.equipment;
            if (!e) return '';
            const url = e.equipment_image || e.image_url || e.image;
            if (!url) return '';
            if (url.startsWith('http')) return url;
            return url.startsWith('/') ? API_BASE.replace(/\/$/, '') + url : API_BASE + url;
        },
        hasMaintenanceSchedule() {
            const e = this.equipment;
            if (!e) return false;
            return e.daily === '1' || e.weekly === '1' || e.monthly === '1' || e.yearly === '1';
        },
    },
    mounted() {
        if (!this.equipment) {
            const raw = sessionStorage.getItem('selectedEquipment');
            if (raw) {
                try {
                    this.$store.commit('setSelectedEquipment', JSON.parse(raw));
                } catch (_) {}
            }
        }
    },
};
</script>

<style scoped>
.equipment-profile-page {
    min-height: 60vh;
}
.profile-empty {
    text-align: center;
    padding: 4rem 2rem;
}
.profile-empty p {
    margin-bottom: 1rem;
    font-size: 1.1rem;
    color: #6b7280;
}

.profile-back-row {
    display: flex;
    justify-content: flex-start;
}
.profile-rtl .profile-back-row {
    justify-content: flex-end;
}
.back-btn {
    border-radius: 10px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

/* صورة المعدة */
.profile-image-section {
    margin-bottom: 2rem;
}
.profile-image-wrapper {
    width: 100%;
    max-width: 560px;
    margin: 0 auto;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
    background: #f8fafc;
}
.profile-image {
    width: 100%;
    height: auto;
    max-height: 320px;
    object-fit: cover;
    display: block;
}
.profile-image-placeholder {
    width: 100%;
    min-height: 280px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: linear-gradient(145deg, #e2e8f0 0%, #f1f5f9 100%);
    color: #94a3b8;
}
.placeholder-icon {
    margin-bottom: 0.5rem;
}
.placeholder-text {
    font-size: 1rem;
    font-weight: 500;
}

/* أقسام المعلومات */
.profile-sections {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}
.profile-section-card {
    background: #fff;
    border-radius: 16px;
    padding: 1.5rem 1.75rem;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(0, 0, 0, 0.05);
}
.section-title {
    font-family: 'Somar-Bold';
    font-size: 1.2rem;
    color: #1e3a5f;
    margin-bottom: 1.25rem;
    padding-bottom: 0.75rem;
    border-bottom: 2px solid rgba(60, 137, 206, 0.2);
    display: flex;
    align-items: center;
    gap: 8px;
}
.section-icon {
    color: #3C89CE;
    font-size: 1.1rem;
}
.section-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 1rem 1.5rem;
}
.section-grid-inline {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    align-items: center;
}
.section-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.section-item-full {
    grid-column: 1 / -1;
}
.section-label {
    font-size: 0.9rem;
    font-weight: 600;
    color: #2B5FA8;
}
.section-value {
    font-size: 1rem;
    color: #374151;
}
.section-value-bold {
    font-weight: 700;
    color: #1f2937;
}
.section-value-mono {
    font-family: monospace;
    font-size: 0.95rem;
}
.status-pill {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 0.9rem;
    font-weight: 600;
    background: linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(22, 163, 74, 0.15) 100%);
    color: #15803d;
}
.schedule-tag {
    padding: 6px 14px;
    border-radius: 10px;
    font-size: 0.9rem;
    font-weight: 600;
    background: rgba(60, 137, 206, 0.12);
    color: #2B6BB5;
}
.section-notes {
    margin: 0;
    line-height: 1.6;
    white-space: pre-wrap;
}
</style>
